import React, { useState } from "react";
import { useAdminPanel } from "./AdminPanelCore";

const AdminActivity = () => {
  const { files, alerts, selectedFile, setSelectedFile, runProcess, dayjs } = useAdminPanel();

  const [isLoading, setIsLoading] = useState(false);
  const [localAlerts, setLocalAlerts] = useState([]);

  const selectedFileId = selectedFile ?? null;

  // Alert helper with timeout
  const showLocalAlert = (type, msg, timeout = 5000) => {
    const id = Date.now();
    setLocalAlerts((prev) => [...prev, { id, type, msg }]);

    setTimeout(() => {
      setLocalAlerts((prev) => prev.filter((a) => a.id !== id));
    }, timeout);
  };

  const handleCheckboxChange = (fileId) => {
    setSelectedFile(fileId === selectedFileId ? null : fileId);
  };

  const handleRunActivity = async () => {
    if (!selectedFileId) return;
  
    try {
      setIsLoading(true);
  
      const result = await runProcess("activity");
  
      const updatedCount = parseInt(result?.rowsInserted ?? 0);
  
      // Handle possible duplicate or no new rows case
      if (updatedCount === 0) {
        showLocalAlert(
          "warning",
          "⚠️ Duplicate data: All activity rows already exist in the database."
        );
      } else {
        showLocalAlert(
          "success",
          `✅ ACTIVITY updated – ${updatedCount} row${updatedCount === 1 ? "" : "s"} updated.`
        );
      }
  
    } catch (err) {
      console.error("❌ Activity update failed:", err);
  
      const errMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Unknown error occurred";
  
      showLocalAlert("danger", `❌ Failed to update activity: ${errMsg}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const sortedFiles = [...files].sort(
    (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
  );

  return (
    <div className="container py-5">
      {/* Alerts */}
      {[...alerts, ...localAlerts].map((a) => (
        <div key={a.id} className={`alert alert-${a.type} mb-4`}>
          {a.msg}
        </div>
      ))}

      <div className="card shadow-sm">
        <div className="card-body">
          <h4 className="card-title mb-4">📊 Select Files to Update Activity</h4>

          <div className="row">
            {sortedFiles.length > 0 ? (
              sortedFiles.map((file) => (
                <div className="col-md-6 col-lg-4 mb-3" key={file.id}>
                  <div className="form-check border rounded p-3 h-100">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`file-${file.id}`}
                      checked={selectedFileId === file.id}
                      onChange={() => handleCheckboxChange(file.id)}
                      disabled={isLoading}
                    />
                    <label className="form-check-label ms-2" htmlFor={`file-${file.id}`}>
                      <strong>{file.name}</strong>
                      <br />
                      <small className="text-muted">
                        {dayjs
                          .utc(file.uploadedAt, "YYYY-MM-DD HH:mm:ss")
                          .format("DD MMM YYYY HH:mm")}
                      </small>
                    </label>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-muted">No files uploaded yet.</div>
            )}
          </div>

          {/* Run Button */}
          <div className="mt-4 text-end">
            <button
              className="btn btn-success px-4"
              disabled={isLoading || !selectedFileId}
              onClick={handleRunActivity}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : (
                "▶ Run Activity"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default AdminActivity;
